import { PanoramaWebSocketApi } from 'api-validations/websocket'
import { z } from 'zod'

import { commaSeparatedStrings, isoDatetimeToDate, jsonCodec } from '../zod-extra'
import { feedbackStepSchema } from './feedback'
import { agentDirectiveSchema, permissionRequestSchema } from './voice-command'

/**
 * Actions clients send to the server. `Unknown` is a fallback for
 * unrecognized actions in the request envelope's discriminated union.
 */
export enum AuroraRequestAction {
  GetSproutStreamSummary = 'GetSproutStreamSummary',
  Chat = 'Chat',
  ListConversations = 'ListConversations',
  GetConversation = 'GetConversation',
  TranscribeSpeech = 'TranscribeSpeech',
  StopSpeechTranscription = 'StopSpeechTranscription',
  StartRealtimeConversation = 'StartRealtimeConversation',
  StopRealtimeConversation = 'StopRealtimeConversation',
  PrepareRealtimeCall = 'PrepareRealtimeCall',
  PrepareLiveCall = 'PrepareLiveCall',
  SetContext = 'SetContext',
  SetSupportedTools = 'SetSupportedTools',
  SubmitDispatchResult = 'SubmitDispatchResult',
  GrantToolApproval = 'GrantToolApproval',
  AttachRealtimeCall = 'AttachRealtimeCall',
  GetExportedFileUrl = 'GetExportedFileUrl',
  Unknown = 'Unknown'
}

/**
 * Actions the server sends in response messages.
 * The action on a response always equals the action on the corresponding request.
 */
export enum AuroraResponseAction {
  GetSproutStreamSummary = 'GetSproutStreamSummary',
  Chat = 'Chat',
  ListConversations = 'ListConversations',
  GetConversation = 'GetConversation',
  TranscribeSpeech = 'TranscribeSpeech',
  StopSpeechTranscription = 'StopSpeechTranscription',
  StartRealtimeConversation = 'StartRealtimeConversation',
  StopRealtimeConversation = 'StopRealtimeConversation',
  PrepareRealtimeCall = 'PrepareRealtimeCall',
  PrepareLiveCall = 'PrepareLiveCall',
  SetContext = 'SetContext',
  SetSupportedTools = 'SetSupportedTools',
  SubmitDispatchResult = 'SubmitDispatchResult',
  GrantToolApproval = 'GrantToolApproval',
  AttachRealtimeCall = 'AttachRealtimeCall',
  GetExportedFileUrl = 'GetExportedFileUrl'
}

export enum InputSource {
  Text = 'text',
  Voice = 'voice'
}

/**
 * Top-level response discriminator for all Aurora server → client messages.
 * This lets consumers narrow on a single field before inspecting the
 * action-specific payload.
 */
export enum AuroraResponseMessageType {
  Terminal = 'terminal',
  Intermediate = 'intermediate',
  Error = 'error',
  Push = 'push',
  /**
   * Server-initiated request that expects a reply (via SubmitDispatchResult).
   * "Dispatch" — chosen over "ServerRequest" because the latter is ambiguous
   * about direction — means "cloud dispatches work to the client."
   */
  Dispatch = 'dispatch'
}

export enum AuroraPushAction {
  AgentNotification = 'AgentNotification',
  AudioStart = 'AudioStart',
  AudioEnd = 'AudioEnd',
  RealtimeInterrupted = 'RealtimeInterrupted',
  PermissionRequest = 'PermissionRequest',
  LiveTranscriptUpdated = 'LiveTranscriptUpdated',
  LiveTranscriptClosed = 'LiveTranscriptClosed'
}

export enum AuroraClientType {
  Dbm = 'dbm',
  Web = 'web',
  Mobile = 'mobile'
}

/**
 * `commitSha` is the commit the connected client was built from: a Panorama
 * monorepo commit for web and mobile, and for a 20|20 a commit of the `2020`
 * parent repository, whose submodule pointers pin every part of that release.
 * Tools that answer questions about client behaviour read it so they describe
 * the software this person is running rather than whatever is on the default
 * branch — production trails `develop` by an unknown number of commits, a
 * mobile build in the field can be much older still, and a display can be
 * several releases behind.
 *
 * `enabledFeatureFlags` completes that picture for web and mobile: the flags
 * turned on in this client, by `FeatureFlag` member name, so those tools leave
 * out functionality the user cannot reach. An empty list means no flags are
 * on, while an absent one means the client did not say, so the tools cannot
 * tell what is gated.
 */
export const auroraClientInfoSchema = z.discriminatedUnion('clientType', [
  z.object({
    clientType: z.literal(AuroraClientType.Dbm),
    serialNumber: z.string(),
    commitSha: z.string().optional()
  }),
  z.object({
    clientType: z.literal(AuroraClientType.Web),
    commitSha: z.string().optional(),
    enabledFeatureFlags: commaSeparatedStrings.optional()
  }),
  z.object({
    clientType: z.literal(AuroraClientType.Mobile),
    commitSha: z.string().optional(),
    enabledFeatureFlags: commaSeparatedStrings.optional()
  })
])

export type AuroraClientInfo = z.infer<typeof auroraClientInfoSchema>

export const auroraSproutStreamSummaryWebSocketApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.GetSproutStreamSummary),
    streamId: z.string()
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.GetSproutStreamSummary),
      /**
       * A terminal payload must be a plain object (only intermediate payloads
       * may be discriminated unions), so `summary` cannot be tied to `ready` in
       * the schema — the server sends it only with `ready: true`. `ready: false`
       * is a cold stream the client polls on; a failed pass is an error frame,
       * not a terminal here.
       */
      ready: z.boolean(),
      summary: z.string().optional()
    })
  })
})

export enum MessageDisposition {
  Informational = 'informational',
  RequiresResponse = 'requires_response',
  Error = 'error'
}

export enum ChatMessageType {
  Text = 'text',
  Options = 'options',
  Approval = 'approval',
  PermissionRequest = 'permission-request',
  Feedback = 'feedback'
}

export enum ConversationRole {
  User = 'user',
  AIAgent = 'ai'
}

export const EXPORTED_FILE_RETENTION_DAYS = 30

export enum ExportedFileStatus {
  Ready = 'ready',
  Expired = 'expired'
}

export const exportedFileSchema = z.object({
  fileId: z.uuid(),
  filename: z.string(),
  rowCount: z.number().int()
})

export type ExportedFile = z.infer<typeof exportedFileSchema>

const textChatMessageSchema = z.discriminatedUnion('role', [
  z.object({
    type: z.literal(ChatMessageType.Text),
    role: z.literal(ConversationRole.User),
    text: z.string(),
    timestamp: isoDatetimeToDate.optional()
  }),
  z.object({
    type: z.literal(ChatMessageType.Text),
    role: z.literal(ConversationRole.AIAgent),
    text: z.string(),
    messageId: z.string().optional(),
    files: z.array(exportedFileSchema).optional(),
    timestamp: isoDatetimeToDate.optional()
  })
])

const optionsResponseSchema = z.object({
  type: z.literal(ChatMessageType.Options),
  role: z.literal(ConversationRole.AIAgent),
  text: z.string(),
  options: z.array(z.string()),
  timestamp: isoDatetimeToDate.optional()
})

const permissionRequestMessageSchema = z.object({
  type: z.literal(ChatMessageType.PermissionRequest),
  role: z.literal(ConversationRole.AIAgent),
  text: z.string(),
  requests: z.array(permissionRequestSchema).min(1),
  timestamp: isoDatetimeToDate.optional()
})

export type PermissionRequestMessage = z.infer<typeof permissionRequestMessageSchema>

const approvalDecisionSchema = z.object({
  id: z.string(),
  approved: z.boolean()
})

export type ApprovalDecision = z.infer<typeof approvalDecisionSchema>

const approvalMessageSchema = z.object({
  type: z.literal(ChatMessageType.Approval),
  role: z.literal(ConversationRole.User),
  decisions: z.array(approvalDecisionSchema).min(1),
  timestamp: isoDatetimeToDate.optional()
})

const feedbackMessageSchema = z.object({
  type: z.literal(ChatMessageType.Feedback),
  role: z.literal(ConversationRole.User),
  step: feedbackStepSchema,
  timestamp: isoDatetimeToDate.optional()
})

export type FeedbackChatMessage = z.infer<typeof feedbackMessageSchema>

export const chatMessageSchema = z.discriminatedUnion('type', [
  textChatMessageSchema,
  optionsResponseSchema,
  approvalMessageSchema,
  permissionRequestMessageSchema,
  feedbackMessageSchema
])

export const conversationSummarySchema = z.object({
  conversationId: z.string(),
  createdAt: isoDatetimeToDate,
  updatedAt: isoDatetimeToDate,
  preview: z.string(),
  messageCount: z.number()
})

export type ConversationSummary = z.infer<typeof conversationSummarySchema>

export const conversationRecordSchema = z.object({
  conversationId: z.string(),
  locale: z.string(),
  createdAt: isoDatetimeToDate,
  updatedAt: isoDatetimeToDate,
  clientContext: z.unknown().optional(),
  messages: z.array(chatMessageSchema)
})

export type ConversationRecord = z.infer<typeof conversationRecordSchema>

export const auroraListConversationsApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.ListConversations),
    continuationToken: z.string().optional()
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.ListConversations),
      conversations: z.array(conversationSummarySchema),
      continuationToken: z.string().optional()
    })
  })
})

export const auroraGetConversationApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.GetConversation),
    conversationId: z.string()
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.GetConversation),
      conversation: conversationRecordSchema.nullable()
    })
  })
})

export const auroraGetExportedFileUrlApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.GetExportedFileUrl),
    fileId: z.uuid()
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.GetExportedFileUrl),
      download: z.discriminatedUnion('status', [
        z.object({ status: z.literal(ExportedFileStatus.Ready), url: z.string() }),
        z.object({ status: z.literal(ExportedFileStatus.Expired) })
      ])
    })
  })
})

export const auroraChatApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z
    .object({
      action: z.literal(AuroraRequestAction.Chat),
      /**
       * The newest message in the conversation. The server reconstructs the
       * prior turns from stored history via `conversationId`.
       */
      message: chatMessageSchema.optional(),
      /**
       * Full conversation history. Legacy payload shape — older clients
       * (notably the 20|20 DBM, which ships on its own firmware cycle) send
       * the entire conversation each turn, so this must remain accepted.
       */
      conversation: z.array(chatMessageSchema).optional(),
      conversationId: z.string().optional(),
      inputSource: z.enum(InputSource).optional(),
      speak: z.boolean().optional()
    })
    .refine((data) => (data.message === undefined) !== (data.conversation === undefined), {
      message: 'Exactly one of message or conversation must be provided'
    }),
  intermediateSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Intermediate,
    data: z.object({
      action: z.literal(AuroraResponseAction.Chat),
      textDelta: z.string()
    })
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.Chat),
      messageId: z.string(),
      conversationId: z.string(),
      message: z.object({
        text: z.string(),
        disposition: z.enum(MessageDisposition),
        // Unlike chatMessageSchema's timestamp (which stays optional for
        // history predating this field), every code path that builds this
        // terminal reply stamps a fresh timestamp — see buildReplyFromResult
        // and the "no pending action to confirm" branch in chat.ts.
        timestamp: isoDatetimeToDate,
        files: z.array(exportedFileSchema).optional()
      }),
      directives: z.array(agentDirectiveSchema)
    })
  })
})

export type AuroraChatMessage = z.infer<typeof chatMessageSchema>
export type AuroraChatConversation = AuroraChatMessage[]
/**
 * The chat terminal response's payload (envelope fields stripped). This is
 * what handlers return; the router stamps the envelope on the way out.
 */
export type AuroraChatReply = Omit<
  z.infer<typeof auroraChatApi.terminalSchema>['data'],
  'action' | 'messageId'
>

export const transcriptEventSchema = z.object({
  transcript: z.string(),
  isFinal: z.boolean(),
  speechFinal: z.boolean(),
  words: z.array(
    z.object({
      word: z.string(),
      start: z.number(),
      end: z.number(),
      confidence: z.number()
    })
  )
})

export type TranscriptEvent = z.infer<typeof transcriptEventSchema>

export const auroraTranscribeSpeechApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.TranscribeSpeech)
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.TranscribeSpeech)
    })
  }),
  intermediateSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Intermediate,
    data: transcriptEventSchema.extend({
      action: z.literal(AuroraResponseAction.TranscribeSpeech)
    })
  })
})

export const auroraStopSpeechTranscriptionApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.StopSpeechTranscription)
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.StopSpeechTranscription)
    })
  })
})

/**
 * Wire schemas for StartRealtimeConversation intermediate events. Two event
 * types share a single action value — discriminate on `type` to narrow:
 *
 * - `inputTranscript`: what the user said, from Realtime API input transcription.
 * - `outputTranscriptDelta`: a text chunk of Aurora's spoken response.
 */
const realtimeInputTranscriptSchema = z.object({
  action: z.literal(AuroraResponseAction.StartRealtimeConversation),
  type: z.literal('inputTranscript'),
  transcript: z.string(),
  isFinal: z.boolean()
})

const realtimeOutputTranscriptDeltaSchema = z.object({
  action: z.literal(AuroraResponseAction.StartRealtimeConversation),
  type: z.literal('outputTranscriptDelta'),
  delta: z.string(),
  /**
   * Identifies which server turn this delta belongs to. Turns whose audio is
   * queued gaplessly behind the previous turn's do not get a separate
   * AudioStart-driven boundary on the client, so this is the only reliable
   * way to split transcript text per turn.
   */
  turnId: z.string()
})

export const realtimeConversationIntermediateSchema = z.discriminatedUnion('type', [
  realtimeInputTranscriptSchema,
  realtimeOutputTranscriptDeltaSchema
])

export type RealtimeConversationInputTranscript = z.infer<typeof realtimeInputTranscriptSchema>
export type RealtimeConversationOutputTranscriptDelta = z.infer<
  typeof realtimeOutputTranscriptDeltaSchema
>

/**
 * Opens a realtime voice conversation session. Unlike Chat, a single request
 * spans the entire conversation: the server holds the request open while the
 * Realtime API session is active, streaming audio and transcript intermediates
 * back as the conversation progresses. The terminal fires when the session ends.
 *
 * Audio reaches the server via binary frames (AuroraBinaryAction.TranscribeSpeech),
 * same as the existing transcription path. Audio responses come back the same way
 * (AudioStart push → binary AudioChunk frames → AudioEnd push).
 *
 * Send StopRealtimeConversation to end the session from the client side.
 */
export const auroraStartRealtimeConversationApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.StartRealtimeConversation),
    conversationId: z.string().optional(),
    detectDirectives: z.boolean().optional()
  }),
  intermediateSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Intermediate,
    data: realtimeConversationIntermediateSchema
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.StartRealtimeConversation),
      conversationId: z.string()
    })
  })
})

export const auroraStopRealtimeConversationApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.StopRealtimeConversation)
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.StopRealtimeConversation)
    })
  })
})

/**
 * Prepares a WebRTC call, which the browser then places itself: picks the
 * conversation the call is saved to, and mints a short-lived OpenAI Realtime
 * client secret (`ek_...`) so the browser can open an `RTCPeerConnection`
 * directly to OpenAI, instead of relaying audio through this server's
 * WebSocket.
 */
export const auroraPrepareRealtimeCallApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.PrepareRealtimeCall),
    /**
     * Continues an existing conversation: the call starts from its saved turns
     * and is saved into it. Omit to start a new conversation.
     */
    conversationId: z.string().optional()
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.PrepareRealtimeCall),
      value: z.string(),
      expiresAt: z.number(),
      /** The conversation this call's transcript is saved to. */
      conversationId: z.string(),
      /**
       * The model `value` above was minted for — the browser's own
       * `RealtimeSession` must be configured with this same model, or the
       * SDP handshake fails. Reading it from here rather than hardcoding it
       * client-side means a model bump on this server takes effect
       * immediately, even for a browser tab still running an older,
       * independently-deployed build of the web bundle.
       */
      model: z.string(),
      /**
       * Bearer token for Aurora's MCP tool server (POST /mcp) — an opaque,
       * randomly-generated string, not a signed claim set. Verifying it is
       * a lookup against McpSessionTokenStore, not a signature check.
       * Unlike `value` above (single-use, just for the WebRTC handshake),
       * this has to stay valid for as long as the realtime conversation
       * runs, since it's checked on every tool call for the session's
       * whole lifetime.
       */
      mcpToken: z.string(),
      mcpTokenExpiresAt: z.number(),
      /**
       * Tool names requiring the user's approval before OpenAI will issue
       * their call — Aurora's own MCP tool catalog is the single source of
       * truth for this list, so the client builds its hosted MCP tool
       * config's `requireApproval` list from here rather than keeping a
       * second, hand-maintained copy that could drift out of sync with it.
       */
      approvalRequiredTools: z.array(z.string()),
      /**
       * The caller's own client-dispatched tools, as Realtime function tools
       * with JSON Schema parameters, for the client to run itself.
       */
      clientTools: z.array(
        z.object({
          name: z.string(),
          description: z.string(),
          parameters: z.record(z.string(), z.unknown())
        })
      )
    })
  })
})

export const auroraPrepareLiveCallApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.PrepareLiveCall),
    sdp: z.string().min(1),
    conversationId: z.string().optional()
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.PrepareLiveCall),
      sdp: z.string(),
      sessionId: z.string(),
      conversationId: z.string()
    })
  })
})

/**
 * Optional. Sets an opaque context blob the cloud will inject into the
 * system prompt for subsequent Chat requests on this connection.
 *
 * The context is treated as opaque JSON; the cloud does not interpret
 * specific keys. Sending this replaces (not merges) any previously sent
 * context. To clear, send `{ context: {} }`.
 */
export const auroraSetContextApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.SetContext),
    context: z.record(z.string(), z.unknown())
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.SetContext)
    })
  })
})

/**
 * Optional. Declares the client-dispatched tool IDs this client supports.
 * The cloud composes the agent's per-session tool catalog from base
 * tools + these. Tool IDs unknown to the cloud-side registry are dropped
 * silently with a log warning.
 */
export const auroraSetSupportedToolsApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.SetSupportedTools),
    supportedTools: z.array(z.string())
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.SetSupportedTools)
    })
  })
})

/**
 * The client's reply to a server-initiated Dispatch. Correlates back to
 * the originating Dispatch via dispatchId.
 *
 * The outcome discriminator distinguishes successful execution from
 * client-side errors. Specific error kinds (e.g. unsupported_tool,
 * invalid_args, runtime_failure) belong in the `code` field of
 * `client_error`, not as new outcome variants.
 */
export const auroraSubmitDispatchResultApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.SubmitDispatchResult),
    dispatchId: z.string(),
    outcome: z.discriminatedUnion('status', [
      z.object({
        status: z.literal('success'),
        value: z.unknown()
      }),
      z.object({
        status: z.literal('client_error'),
        code: z.string(),
        message: z.string()
      })
    ])
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.SubmitDispatchResult)
    })
  })
})

/**
 * The client's record of a user approving one specific hosted-MCP tool
 * call — a specific tool name with specific arguments, not a standing
 * permission. Sent after the client has already told OpenAI's Realtime
 * API to proceed (via the SDK's own approval flow); this is Aurora's own,
 * independent record of the same decision, checked when the tool call
 * OpenAI issues actually reaches Aurora's MCP server.
 */
export const auroraGrantToolApprovalApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.GrantToolApproval),
    toolName: z.string(),
    args: z.record(z.string(), z.unknown())
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.GrantToolApproval)
    })
  })
})

/**
 * Asks Aurora to save a WebRTC call the browser has connected to OpenAI into
 * the conversation it was prepared for. Sent over the caller's own connection,
 * so the same server task saves the call's spoken turns and the conversation's
 * typed ones, and sent again over a new connection after a reconnect.
 */
export const auroraAttachRealtimeCallApi = PanoramaWebSocketApi.defineApi({
  type: 'Request',
  requestSchema: z.object({
    action: z.literal(AuroraRequestAction.AttachRealtimeCall),
    /** The call's id, from the `Location` header OpenAI answered the SDP offer with. */
    callId: z.string().min(1),
    /**
     * The MCP token prepared with the call. Aurora reads the call's caller and conversation from
     * it on any server task, so the attach can be sent again after a reconnect.
     */
    mcpToken: z.string()
  }),
  terminalSchema: PanoramaWebSocketApi.createResponseEnvelopeSchema({
    messageType: AuroraResponseMessageType.Terminal,
    data: z.object({
      action: z.literal(AuroraResponseAction.AttachRealtimeCall)
    })
  })
})

const liveTranscriptSegmentSchema = z.object({
  segmentId: z.string(),
  role: z.enum(ConversationRole),
  text: z.string()
})

/**
 * Server-initiated push message. Sent outside any client request
 * lifecycle. No reply is expected — the client routes by action and
 * handles each push as a side effect.
 *
 * Pushes do not carry a requestId (they don't belong to a request) or a
 * correlation ID (no reply to correlate). If trace-ID-style logging
 * becomes a need, it should be added as a cross-cutting field across
 * all message types, not specifically here.
 */
export const auroraPushEnvelopeSchema = z.object({
  messageType: z.literal(AuroraResponseMessageType.Push),
  data: z.discriminatedUnion('action', [
    z.object({
      action: z.literal(AuroraPushAction.AgentNotification),
      text: z.string()
    }),
    z.object({
      action: z.literal(AuroraPushAction.AudioStart),
      messageId: z.string(),
      sampleRate: z.literal(24000)
    }),
    z.object({
      action: z.literal(AuroraPushAction.AudioEnd),
      messageId: z.string()
    }),
    z.object({
      action: z.literal(AuroraPushAction.RealtimeInterrupted),
      messageId: z.string()
    }),
    z.object({
      action: z.literal(AuroraPushAction.PermissionRequest),
      requests: z.array(permissionRequestSchema).min(1)
    }),
    liveTranscriptSegmentSchema.extend({
      action: z.literal(AuroraPushAction.LiveTranscriptUpdated)
    }),
    liveTranscriptSegmentSchema.extend({
      action: z.literal(AuroraPushAction.LiveTranscriptClosed)
    })
  ])
})

/**
 * Server-initiated dispatch. The cloud uses this to ask the client to
 * do something (typically invoke a client-side tool) and awaits a reply
 * via SubmitDispatchResult.
 *
 * dispatchId is a distinct ID namespace from requestId. It is generated
 * cloud-side and echoed back by the client in the corresponding
 * SubmitDispatchResult.
 *
 * The Dispatch envelope corresponds conceptually to a function call the
 * cloud agent is making — the call site happens to be on the client.
 * From the agent's perspective, dispatched tools are interchangeable
 * with cloud-executed tools.
 *
 * `args` is `Record<string, unknown>` rather than `unknown` because the
 * OpenAI Agents SDK requires tool arguments to be a top-level JSON
 * object. Per-tool argument shapes are validated at the dispatch
 * handler boundary, not at this wire envelope.
 */
export const auroraDispatchEnvelopeSchema = z.object({
  messageType: z.literal(AuroraResponseMessageType.Dispatch),
  dispatchId: z.string(),
  data: z.discriminatedUnion('action', [
    z.object({
      action: z.literal('InvokeClientTool'),
      toolId: z.string(),
      args: z.record(z.string(), z.unknown())
    })
  ])
})

export type AnyAuroraWebSocketApi =
  | typeof auroraChatApi
  | typeof auroraListConversationsApi
  | typeof auroraGetConversationApi
  | typeof auroraGetExportedFileUrlApi
  | typeof auroraSproutStreamSummaryWebSocketApi
  | typeof auroraTranscribeSpeechApi
  | typeof auroraStopSpeechTranscriptionApi
  | typeof auroraStartRealtimeConversationApi
  | typeof auroraStopRealtimeConversationApi
  | typeof auroraPrepareRealtimeCallApi
  | typeof auroraPrepareLiveCallApi
  | typeof auroraSetContextApi
  | typeof auroraSetSupportedToolsApi
  | typeof auroraSubmitDispatchResultApi
  | typeof auroraGrantToolApprovalApi
  | typeof auroraAttachRealtimeCallApi

/**
 * Error response schema. Errors may be emitted in response to any request
 * and don't belong to a single API definition.
 */
export const errorResponseSchema = z.object({
  requestId: z.string(),
  messageType: z.literal(AuroraResponseMessageType.Error),
  error: z.string()
})

/**
 * Discriminated union of all terminal payloads.
 * Discriminated on `action`, since each action has exactly one terminal shape.
 */
export const auroraTerminalPayloadSchema = z.discriminatedUnion('action', [
  auroraChatApi.terminalSchema.shape.data,
  auroraListConversationsApi.terminalSchema.shape.data,
  auroraGetConversationApi.terminalSchema.shape.data,
  auroraGetExportedFileUrlApi.terminalSchema.shape.data,
  auroraSproutStreamSummaryWebSocketApi.terminalSchema.shape.data,
  auroraTranscribeSpeechApi.terminalSchema.shape.data,
  auroraStopSpeechTranscriptionApi.terminalSchema.shape.data,
  auroraStartRealtimeConversationApi.terminalSchema.shape.data,
  auroraStopRealtimeConversationApi.terminalSchema.shape.data,
  auroraPrepareRealtimeCallApi.terminalSchema.shape.data,
  auroraPrepareLiveCallApi.terminalSchema.shape.data,
  auroraSetContextApi.terminalSchema.shape.data,
  auroraSetSupportedToolsApi.terminalSchema.shape.data,
  auroraSubmitDispatchResultApi.terminalSchema.shape.data,
  auroraGrantToolApprovalApi.terminalSchema.shape.data,
  auroraAttachRealtimeCallApi.terminalSchema.shape.data
])

/**
 * Discriminated union of all intermediate payloads.
 */
// oxlint-disable-next-line typescript/no-non-null-assertion -- auroraChatApi declares intermediateSchema above, so it is always defined here
const chatIntermediateSchema = auroraChatApi.intermediateSchema!
// oxlint-disable-next-line typescript/no-non-null-assertion -- auroraTranscribeSpeechApi declares intermediateSchema above, so it is always defined here
const transcribeSpeechIntermediateSchema = auroraTranscribeSpeechApi.intermediateSchema!

const realtimeConversationIntermediateEnvelopeSchema =
  // oxlint-disable-next-line typescript/no-non-null-assertion -- auroraStartRealtimeConversationApi declares intermediateSchema above, so it is always defined here
  auroraStartRealtimeConversationApi.intermediateSchema!

// Both realtime intermediate types share action: StartRealtimeConversation, so they
// cannot coexist in a discriminated union on `action`. Use z.union instead.
export const auroraIntermediatePayloadSchema = z.union([
  chatIntermediateSchema.shape.data,
  transcribeSpeechIntermediateSchema.shape.data,
  ...realtimeConversationIntermediateEnvelopeSchema.shape.data.options
])

export const auroraTerminalResponseSchema = PanoramaWebSocketApi.createResponseEnvelopeSchema({
  messageType: AuroraResponseMessageType.Terminal,
  data: auroraTerminalPayloadSchema
})

export const auroraIntermediateResponseSchema = PanoramaWebSocketApi.createResponseEnvelopeSchema({
  messageType: AuroraResponseMessageType.Intermediate,
  data: auroraIntermediatePayloadSchema
})

/**
 * All possible server → client messages. Use this for parsing/logging
 * incoming messages without knowing in advance whether a message is
 * terminal, intermediate, or an error.
 */
export const auroraResponseSchema = z.discriminatedUnion('messageType', [
  errorResponseSchema,
  auroraTerminalResponseSchema,
  auroraIntermediateResponseSchema,
  auroraPushEnvelopeSchema,
  auroraDispatchEnvelopeSchema
])

export type AuroraTerminalPayload = z.infer<typeof auroraTerminalPayloadSchema>
export type AuroraIntermediatePayload = z.infer<typeof auroraIntermediatePayloadSchema>
export type AuroraTerminalResponse = z.infer<typeof auroraTerminalResponseSchema>
export type AuroraIntermediateResponse = z.infer<typeof auroraIntermediateResponseSchema>
export type AuroraErrorResponse = z.infer<typeof errorResponseSchema>
export type AuroraResponse = z.infer<typeof auroraResponseSchema>
export type AuroraResponseWire = z.input<typeof auroraResponseSchema>
export type AuroraSetContextRequest = z.infer<typeof auroraSetContextApi.requestSchema>
export type AuroraSetSupportedToolsRequest = z.infer<
  typeof auroraSetSupportedToolsApi.requestSchema
>
export type AuroraSubmitDispatchResultRequest = z.infer<
  typeof auroraSubmitDispatchResultApi.requestSchema
>
export type AuroraPushEnvelope = z.infer<typeof auroraPushEnvelopeSchema>
export type AuroraDispatchEnvelope = z.infer<typeof auroraDispatchEnvelopeSchema>

/**
 * The Unknown action is included to ensure that the discriminated union
 * in auroraWebSocketRequestSchema covers the `catch` case, even if no client
 * will realistically send it.
 */
const unknownActionSchema = z.object({
  action: z.literal(AuroraRequestAction.Unknown)
})

/**
 * An exhaustive list of all request schemas that Aurora WebSocket API
 * supports.
 */
export const auroraWebsocketRequestSchemaList = [
  auroraSproutStreamSummaryWebSocketApi.requestSchema,
  auroraChatApi.requestSchema,
  auroraListConversationsApi.requestSchema,
  auroraGetConversationApi.requestSchema,
  auroraGetExportedFileUrlApi.requestSchema,
  auroraTranscribeSpeechApi.requestSchema,
  auroraStopSpeechTranscriptionApi.requestSchema,
  auroraStartRealtimeConversationApi.requestSchema,
  auroraStopRealtimeConversationApi.requestSchema,
  auroraPrepareRealtimeCallApi.requestSchema,
  auroraPrepareLiveCallApi.requestSchema,
  auroraSetContextApi.requestSchema,
  auroraSetSupportedToolsApi.requestSchema,
  auroraSubmitDispatchResultApi.requestSchema,
  auroraGrantToolApprovalApi.requestSchema,
  auroraAttachRealtimeCallApi.requestSchema,
  unknownActionSchema
] as const

/**
 * The main schema for validating Aurora WebSocket requests.
 * Any message sent to Aurora over WebSocket should conform to this schema.
 *
 * If the action is unrecognized, the data field will default to
 * `{ action: AuroraRequestAction.Unknown }`.
 *
 * If requestId is missing or not a string, validation will fail.
 */
export const auroraWebSocketRequestSchema = jsonCodec(
  z.object({
    requestId: z.string(),
    data: z.discriminatedUnion('action', auroraWebsocketRequestSchemaList).catch({
      action: AuroraRequestAction.Unknown
    })
  })
)
